import { displaySource } from "../demo"
import { Panel } from "../Panel"
import type { AiMarketImplications as AiMarketData } from "../dashboard-types"

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

function directionArrow(dir: "up" | "down" | "stable"): { glyph: string; cls: string } {
  if (dir === "up") return { glyph: "\u2191", cls: "text-red-400" }
  if (dir === "down") return { glyph: "\u2193", cls: "text-emerald-400" }
  return { glyph: "\u2192", cls: "text-[color:var(--text-muted)]" }
}

function impactBadge(impact: "low" | "medium" | "high"): { bg: string; text: string } {
  if (impact === "high") return { bg: "bg-red-500/15", text: "text-red-400" }
  if (impact === "medium") return { bg: "bg-amber-500/15", text: "text-amber-400" }
  return { bg: "bg-sky-500/15", text: "text-sky-400" }
}

const AI_BADGE = (
  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 uppercase">
    AI
  </span>
)

export function AiMarketImplications({ data, demo }: { data: AiMarketData; demo?: boolean }) {
  return (
    <Panel title="AI Market Implications" demo={demo} badge={AI_BADGE}>
      {data.locked ? (
        <div className="px-3 py-6 flex flex-col items-center gap-3 text-center">
          <span className="text-2xl">&#128274;</span>
          <p className="text-[11px] text-[color:var(--text-muted)]">
            Sign in to unlock premium features
          </p>
          <button type="button" className="btn-pill text-[10px]">
            Sign In
          </button>
        </div>
      ) : (
        <div className="px-3 py-2 flex flex-col gap-3">
          {/* Summary */}
          <p className="text-sm leading-relaxed text-[color:var(--text)]">
            {data.summary}
          </p>

          {/* Key drivers */}
          {data.key_drivers.length > 0 && (
            <div>
              <h4 className="category-heading text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] mb-1.5">
                Key Drivers
              </h4>
              <div className="flex flex-col gap-1">
                {data.key_drivers.map((d) => {
                  const arrow = directionArrow(d.direction)
                  const badge = impactBadge(d.impact)
                  return (
                    <div key={d.factor} className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${arrow.cls}`}>{arrow.glyph}</span>
                      <span className="text-[11px] text-[color:var(--text)] flex-1">{d.factor}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${badge.bg} ${badge.text}`}>
                        {d.impact}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Recommended actions */}
          {data.recommended_actions.length > 0 && (
            <div>
              <h4 className="category-heading text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] mb-1.5">
                Recommended Actions
              </h4>
              <ol className="list-none flex flex-col gap-1">
                {data.recommended_actions.map((action, i) => (
                  <li key={i} className="text-xs font-mono text-[color:var(--text-muted)] flex gap-2">
                    <span className="text-[color:var(--text-dim)] tabular-nums">{i + 1}.</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Footer — fabricated model attributions are scrubbed; simulated
              analysis is labelled as such. */}
          <div className="text-[9px] text-[color:var(--text-dim)] uppercase tracking-wider">
            {data.demo || demo
              ? "Simulated forecast"
              : `generated via ${displaySource(data.model, "Simulated forecast")}`}{" "}
            · {relTime(data.generated_at)}
          </div>
        </div>
      )}
    </Panel>
  )
}
